import type { ComponentType, ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** Uppercase, small-caps field label — matches the /profile field style. */
export const FIELD_LABEL =
  "text-muted-foreground text-xs font-semibold tracking-wider uppercase";

type IconType = ComponentType<{ className?: string }>;

interface SettingsSectionProps {
  title: string;
  icon: IconType;
  children: ReactNode;
  /** Body className — defaults to the responsive, top-aligned field grid. */
  bodyClassName?: string;
  /** Scroll anchor id (for a Jump-to-Section nav, when used). */
  id?: string;
  /** Secondary line under the title (e.g. what the section controls). */
  subtitle?: string;
  /** Right-aligned header slot — a status pill, a "Manage …" link, etc. */
  headerAction?: ReactNode;
  /** When set, the icon renders in a coloured 40px well with these classes
   * (e.g. `bg-accent text-accent-foreground`); otherwise it's the inline icon. */
  iconWellClassName?: string;
}

/**
 * A titled section card for settings/profile forms — a Card with a muted icon
 * header and a responsive field grid. The single visual pattern all form
 * sections share (see /profile). An optional {@link SettingsSectionProps.subtitle},
 * {@link SettingsSectionProps.headerAction} and {@link SettingsSectionProps.iconWellClassName}
 * opt into the richer Company/Branding header without changing other pages.
 */
export function SettingsSection({
  title,
  icon: Icon,
  children,
  bodyClassName,
  id,
  subtitle,
  headerAction,
  iconWellClassName,
}: SettingsSectionProps) {
  return (
    <Card id={id} className="scroll-mt-24 gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex items-center gap-3">
          {iconWellClassName && (
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-sm",
                iconWellClassName,
              )}
            >
              <Icon className="size-[18px]" />
            </span>
          )}
          <div className="flex flex-col">
            <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
              {!iconWellClassName && <Icon className="text-primary size-5" />} {title}
            </h3>
            {subtitle && <p className="text-muted-foreground text-[13px]">{subtitle}</p>}
          </div>
        </div>
        {headerAction}
      </div>
      <div
        className={cn(
          "grid grid-cols-1 items-start gap-6 p-6 md:grid-cols-2 lg:grid-cols-3",
          bodyClassName,
        )}
      >
        {children}
      </div>
    </Card>
  );
}

/** In-card sub-section divider (icon + label + optional scroll anchor). Use for
 * multi-section forms rendered inside a single {@link SettingsSection}-style card. */
export function SettingsSubHeader({
  id,
  title,
  icon: Icon,
}: {
  id?: string;
  title: string;
  icon: IconType;
}) {
  return (
    <div id={id} className="scroll-mt-24 border-t px-6 py-3">
      <h4 className="text-foreground flex items-center gap-2 text-sm font-bold">
        <Icon className="text-primary size-4" /> {title}
      </h4>
    </div>
  );
}

/** Right-aligned, bordered footer row for a card's primary action(s). */
export function SettingsFormActions({ children }: { children: ReactNode }) {
  return <div className="flex justify-end border-t px-6 py-4">{children}</div>;
}

/**
 * Sticky, edge-to-edge action bar pinned to the bottom of the viewport while a
 * form page scrolls — mirrors the client-profile save bar. The `-mx-6`
 * cancels the app shell's `p-6` so the bar spans the full content width.
 */
export function SettingsStickyBar({ children }: { children: ReactNode }) {
  return (
    <div className="bg-card sticky bottom-0 z-10 -mx-6 flex flex-wrap items-center justify-end gap-3 border-t px-6 py-4">
      {children}
    </div>
  );
}
